import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { calculateCabinMetrics, calculateRecommendedGalleys } from '../../../domain/economics';
import type { AircraftType, CabinConfiguration } from '../../../domain/models';
import { ActionBar } from './ActionBar';
import { AmenityControls } from './AmenityControls';
import { ClassControls } from './ClassControls';
import { FloorBudgetBar } from './FloorBudgetBar';
import { MetricsPanel } from './MetricsPanel';
import { PresetBar } from './PresetBar';
import { ValidationAlerts } from './ValidationAlerts';
import type {
  CabinApplyTarget,
  CabinEditorCommand,
  QuickPresetId,
  SavedCabinPreset,
} from './cabinEditorTypes';
import { QUICK_PRESETS, buildQuickPreset } from './cabinPresetBuilders';
import type { CabinPresetStore } from './useCabinPresets';
import { useCabinDraft } from './useCabinDraft';

export interface CabinWorkbenchProps {
  readonly type: AircraftType;
  /** Layout the draft starts from; also what "Reset changes" returns to. */
  readonly baseline: CabinConfiguration;
  readonly target: CabinApplyTarget;
  readonly presetStore: CabinPresetStore;
  readonly onCommand: (command: CabinEditorCommand) => void;
  readonly onPresetSaved?: (preset: SavedCabinPreset) => void;
}

function buildCommand(target: CabinApplyTarget, cabin: CabinConfiguration): CabinEditorCommand {
  if (target.kind === 'ACQUISITION') {
    return {
      type: 'ACQUIRE_AIRCRAFT',
      typeId: target.typeId,
      purchaseType: target.mode,
      cabin,
    };
  }
  return { type: 'REFIT_AIRCRAFT', registration: target.registration, cabin };
}

/**
 * Editing surface for one aircraft. All derived numbers come from the pure
 * Step 3 domain functions; the only output is a `CabinEditorCommand`.
 * Remount it (via `key`) to start over from a different baseline.
 */
export function CabinWorkbench({
  type,
  baseline,
  target,
  presetStore,
  onCommand,
  onPresetSaved,
}: CabinWorkbenchProps): ReactElement {
  const draft = useCabinDraft(baseline);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const { cabin } = draft;

  const metrics = useMemo(() => calculateCabinMetrics(type, cabin), [type, cabin]);
  const recommendedGalleys = useMemo(() => calculateRecommendedGalleys(cabin), [cabin]);

  const savedForType = useMemo(
    () => presetStore.presets.filter((preset) => preset.typeId === type.id),
    [presetStore.presets, type.id]
  );

  const handleApply = useCallback((): void => {
    if (!metrics.isValid) {
      return;
    }
    onCommand(buildCommand(target, cabin));
    setStatusMessage(
      target.kind === 'ACQUISITION'
        ? `Acquisition order for ${type.id} sent with this cabin.`
        : `Refit for ${target.registration} sent to the simulation.`
    );
  }, [cabin, metrics.isValid, onCommand, target, type.id]);

  const handleQuickPreset = useCallback(
    (id: QuickPresetId): void => {
      draft.replace(buildQuickPreset(id, type));
      setStatusMessage(null);
    },
    [draft, type]
  );

  const handleSavedPreset = useCallback(
    (preset: SavedCabinPreset): void => {
      draft.replace(preset.cabin);
      setStatusMessage(null);
    },
    [draft]
  );

  const handleSavePreset = useCallback(
    (name: string): boolean => {
      if (!metrics.isValid) {
        return false;
      }
      const saved = presetStore.savePreset(name, type.id, cabin);
      if (saved === null) {
        return false;
      }
      onPresetSaved?.(saved);
      return true;
    },
    [cabin, metrics.isValid, onPresetSaved, presetStore, type.id]
  );

  const handleReset = useCallback((): void => {
    draft.replace(baseline);
    setStatusMessage(null);
  }, [baseline, draft]);

  return (
    <div className="ce-workbench">
      <div className="ce-column ce-column--controls">
        <FloorBudgetBar metrics={metrics} />
        <ClassControls
          type={type}
          cabin={cabin}
          metrics={metrics}
          onSeatsChange={draft.setSeats}
          onEconomyPitchChange={draft.setEconomyPitch}
          onBusinessPitchChange={draft.setBusinessPitch}
        />
        <AmenityControls
          cabin={cabin}
          totalSeats={metrics.totalSeats}
          recommendedGalleys={recommendedGalleys}
          onWifiChange={draft.setWifi}
          onPowerChange={draft.setPower}
          onGalleysChange={draft.setGalleys}
        />
        <PresetBar
          quickPresets={QUICK_PRESETS}
          savedPresets={savedForType}
          canSave={metrics.isValid}
          onApplyQuick={handleQuickPreset}
          onApplySaved={handleSavedPreset}
          onDeleteSaved={presetStore.deletePreset}
          onSave={handleSavePreset}
        />
      </div>

      <div className="ce-column ce-column--results">
        <ValidationAlerts issues={metrics.issues} isValid={metrics.isValid} />
        <MetricsPanel type={type} metrics={metrics} />
        <ActionBar
          target={target}
          canApply={metrics.isValid}
          isDirty={draft.isDirty}
          statusMessage={statusMessage}
          onApply={handleApply}
          onReset={handleReset}
        />
      </div>
    </div>
  );
}
